import styled from 'styled-components';

import { rem } from '../style';
import { Box } from '../design-system/box/box.style';
import { grey5 } from './colours.style';
import { borderRadius16, boxShadowBlack12 } from './borders.style';

type BoxPanelProps = {
  $borderRadius?: number;
  $borderTopLeftRadius?: number;
  $borderTopRightRadius?: number;
  $borderBottomRightRadius?: number;
  $borderBottomLeftRadius?: number;
  $border?: string; // Override entire border property
  $borderTop?: string;
  $borderRight?: string;
  $borderBottom?: string;
  $borderLeft?: string;
  $borderColor?: string; // Override just the color while keeping default width/style
  $backgroundColor?: string; // Background color for the container
  $boxShadow?: boolean; // Apply box shadow (boxShadowBlack12)
  $isOverflowVisible?: boolean;
};

/**
 * A styled Box component with default border styling and customizable border properties.
 *
 * Extends the base Box component with a default grey border (1px solid grey5) and
 * border-radius (16px). Provides comprehensive border customization options including
 * individual border sides, colors, and radius values. Automatically applies 12px
 * padding by default and includes overflow: hidden for contained content.
 *
 * @param $borderRadius - Custom border radius in pixels (default: borderRadius16)
 * @param $borderTopLeftRadius - Custom top-left border radius in pixels
 * @param $borderTopRightRadius - Custom top-right border radius in pixels
 * @param $borderBottomRightRadius - Custom bottom-right border radius in pixels
 * @param $borderBottomLeftRadius - Custom bottom-left border radius in pixels
 * @param $border - Override entire border property with custom CSS border value
 * @param $borderTop - Custom top border CSS value
 * @param $borderRight - Custom right border CSS value
 * @param $borderBottom - Custom bottom border CSS value
 * @param $borderLeft - Custom left border CSS value
 * @param $borderColor - Override border color while keeping default width and style
 * @param $backgroundColor - Background color for the container
 * @param $boxShadow - Apply box shadow (boxShadowBlack12) for elevation effect
 * @param $isOverflowVisible - Apply overflow: visible to the container
 *
 * @see {@link [STORYBOOK_URL]http://designsystem.inquisitive.s3-website-ap-southeast-2.amazonaws.com/?path=/story/design-system-elements-boxpanel--default}
 *
 * @example
 * ```tsx
 * // Basic usage with default styling
 * <BoxPanel>
 *   <p>Content with default border and padding</p>
 * </BoxPanel>
 *
 * // Custom border radius and color
 * <BoxPanel $borderRadius={8} $borderColor="#ff0000">
 *   <h2>Red bordered container</h2>
 * </BoxPanel>
 *
 * // With background color
 * <BoxPanel $backgroundColor="#f0f8ff" $borderColor="#4a90e2">
 *   <p>Light blue background with blue border</p>
 * </BoxPanel>
 *
 * // With box shadow for elevation
 * <BoxPanel $boxShadow $backgroundColor="#ffffff">
 *   <h3>Elevated panel with shadow</h3>
 * </BoxPanel>
 *
 * // Individual corner radius customization
 * <BoxPanel
 *   $borderTopLeftRadius={0}
 *   $borderTopRightRadius={0}
 *   $borderBottomLeftRadius={16}
 *   $borderBottomRightRadius={16}
 * >
 *   <div>Rounded bottom corners only</div>
 * </BoxPanel>
 *
 * // Custom individual borders
 * <BoxPanel
 *   $borderTop="2px solid blue"
 *   $borderBottom="1px dashed red"
 *   $borderLeft="none"
 *   $borderRight="none"
 * >
 *   <span>Top and bottom borders only</span>
 * </BoxPanel>
 * ```
 */
export const BoxPanel = styled(Box).attrs(props => ({
  $padding: props.$padding !== undefined ? props.$padding : 12,
}))<BoxPanelProps>`
  /* Default border - can be overridden */
  border: 1px solid ${grey5};

  /* Apply border overrides */
  ${({ $border }) => $border && `border: ${$border};`}
  ${({ $borderTop }) => $borderTop && `border-top: ${$borderTop};`}
  ${({ $borderRight }) => $borderRight && `border-right: ${$borderRight};`}
  ${({ $borderBottom }) => $borderBottom && `border-bottom: ${$borderBottom};`}
  ${({ $borderLeft }) => $borderLeft && `border-left: ${$borderLeft};`}
  ${({ $borderColor }) => $borderColor && `border-color: ${$borderColor};`}
  
  /* Apply background color */
  ${({ $backgroundColor }) =>
    $backgroundColor && `background-color: ${$backgroundColor};`}
  
  /* Apply box shadow */
  ${({ $boxShadow }) => $boxShadow && boxShadowBlack12}
  
  /* Default border radius - can be overridden */
  border-radius: ${({ $borderRadius = borderRadius16 }) => rem($borderRadius)};

  /* Apply individual border radius overrides */
  ${({ $borderTopLeftRadius }) =>
    $borderTopLeftRadius !== undefined &&
    `border-top-left-radius: ${rem($borderTopLeftRadius)};`}
  ${({ $borderTopRightRadius }) =>
    $borderTopRightRadius !== undefined &&
    `border-top-right-radius: ${rem($borderTopRightRadius)};`}
  ${({ $borderBottomRightRadius }) =>
    $borderBottomRightRadius !== undefined &&
    `border-bottom-right-radius: ${rem($borderBottomRightRadius)};`}
  ${({ $borderBottomLeftRadius }) =>
    $borderBottomLeftRadius !== undefined &&
    `border-bottom-left-radius: ${rem($borderBottomLeftRadius)};`}
  
  overflow: ${({ $isOverflowVisible }) =>
    $isOverflowVisible ? 'visible' : 'hidden'};
`;
